import { useParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { LoadingPage } from '../../loading-page';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { IInflationIndex, IInflationIndexValue } from '../../../../shared/services/inflation-index/inflation-index.model';
import InflationIndexService from '../../../../shared/services/inflation-index/inflation-index.service';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';

const inflationIndexService = new InflationIndexService();

export const SuperAdminInflationIndexDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const userNotificationsContext = useContext(UserNotificationsContext);

  const [index, setIndex] = useState<IInflationIndex | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [change, setChange] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);

  const [csvInput, setCsvInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    inflationIndexService.getById(id)
      .then(setIndex)
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [id, change]);

  const handleDeleteValue = async (year: number) => {
    if (!id) return;
    try {
      await inflationIndexService.deleteValue(id, year);
      setChange(c => c + 1);
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpsertFromCsv = async () => {
    if (!id || !csvInput.trim()) return;
    setIsSubmitting(true);
    try {
      const values: IInflationIndexValue[] = csvInput.trim().split('\n').map(line => {
        const [yearStr, valueStr] = line.split(',').map(s => s.trim());
        return { year: parseInt(yearStr, 10), value: parseFloat(valueStr) };
      }).filter(v => !isNaN(v.year) && !isNaN(v.value));

      if (values.length === 0) return;
      await inflationIndexService.upsertValues(id, values);
      setCsvInput('');
      setChange(c => c + 1);
      userNotificationsContext.openSnackbar({ open: true, message: `${values.length} year values saved` });
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const headCells: IHeadCell[] = [
    { id: 'year', label: 'Year', numeric: false, sortable: false },
    { id: 'value', label: 'Index Value', numeric: true, sortable: false },
    { id: 'actions', label: '', numeric: false, sortable: false },
  ];

  const rows = (index?.values ?? [])
    .slice()
    .sort((a, b) => b.year - a.year)
    .map(v => ({
      id: String(v.year),
      year: v.year,
      value: v.value.toFixed(2),
      actions: (
        <IconButton size="small" onClick={() => handleDeleteValue(v.year)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    }));

  return (
    <PageOuterContainer>
      <PageHeading
        title={index?.name ?? 'Inflation Index'}
        includeBackButton={true}
        backButtonLink="/super-admin/inflation-indices"
        backButtonText="Back to Inflation Indices"
      />

      <LoadingPage pageReady={!isLoading}>
        {index && (
          <>
            <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
              <Chip label={`Currency: ${index.currencyCode}`} />
              <Chip label={`Source: ${index.source}`} />
              {index.isDefault && <Chip label="Default" color="primary" />}
              <Chip label={index.isActive ? 'Active' : 'Inactive'} color={index.isActive ? 'success' : 'default'} />
            </Stack>

            <Box mb={4}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Add / Update Year Values
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Paste CSV data (one row per line): year,value (e.g. 2023,131.2)
              </Typography>
              <TextField
                multiline
                minRows={4}
                maxRows={12}
                fullWidth
                placeholder={'2023,131.2\n2022,126.1\n2021,119.5'}
                value={csvInput}
                onChange={e => setCsvInput(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  disabled={!csvInput.trim() || isSubmitting}
                  onClick={handleUpsertFromCsv}
                >
                  {isSubmitting ? 'Saving…' : 'Save values'}
                </Button>
                <Tooltip title="External API adapters not yet implemented. Use CSV upload above">
                  <span>
                    <Button
                      variant="outlined"
                      disabled={true}
                    >
                      {`Fetch from ${index.source}`}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Box>

            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Stored Year Values ({rows.length})
            </Typography>
            <TableDisplay
              headCells={headCells}
              rows={rows}
              selected={selected}
              setSelected={setSelected}
            />
          </>
        )}
      </LoadingPage>
    </PageOuterContainer>
  );
};
